import { Modal } from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Percent, Gift } from "lucide-react";
import { useEcomConfig } from "hooks/useEcomConfig";
import { setSelectedGrandPromo, setSelectedCartPromoId, calculateDiscount } from "State/CartSlice/CartSlice";
import { RootState } from "State/store";
import { useGetAllCartWithIdQuery } from "Services/CartApiSlice";
import { Link } from "react-router-dom";
import { encodeParam } from "Utilities/urlEncoder";
import { isClientPromotionActive } from "Utilities/PromotionDateUtils";
import { useLiveTime } from "hooks/useLiveTime";
import { notifications } from "@mantine/notifications";
import { formatIndianCurrency } from "Utilities/CurrencyFormatter";

interface OrderDiscountSelectorProps {
  discounts: any[];
  baseFinalTotal: number;
  cartPromotions?: any[];
  byQuantityPromoItemsMap?: Record<string, { id: string; qty: number }[]>;
  /** Maps promoKey → source-product info so the unlock hint can name the exact product.
   *  Computed in the caller alongside allCartPromotions. */
  promoSourceMap?: Record<string, Array<{ name: string; categoryName?: string; currentQty: number }>>;
  /** When true, skip auto-select so a second simultaneous instance (e.g. sidebar on checkout page)
   *  doesn't race against the primary instance. Manual user selections still work. */
  disableAutoSelect?: boolean;
}

const CartPromoImage = ({ src, alt, className }: { src?: string; alt?: string; className?: string }) => {
  const [error, setError] = useState(false);
  if (error || !src) {
    return (
      <div className={`bg-gray-100 flex items-center justify-center rounded border border-vintageBorder ${className}`}>
        <span className="text-[10px] text-gray-500">No Image</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setError(true)} />;
};

const OrderDiscountSelector: React.FC<OrderDiscountSelectorProps> = ({
  
  discounts,
  baseFinalTotal,
  cartPromotions = [],
  byQuantityPromoItemsMap = {},
  promoSourceMap = {},
  disableAutoSelect = false,
}) => {
  const dispatch = useDispatch();
  const selectedGrandPromo = useSelector((state: RootState) => state.cart.selectedGrandPromo);
  const reduxCartPromoId = useSelector((state: RootState) => state.cart.selectedCartPromoId);
  const { showPromotion, numberOfDecimal } = useEcomConfig();
  const now = useLiveTime();
  const [opened, setOpened] = useState(false);

  const cartList = useSelector((state: RootState) => state.cart.cartList) || [];
  const cartProductList = useSelector((state: RootState) => state.cart.cartProductList);

  const getCustomerQuantityForPromo = (promo: any): number => {
    const promoId = promo.promotionId || promo.id;
    const products = cartProductList?.products || [];
    for (const item of cartList) {
      const product = products.find((p: any) => p.product.item === item.productId);
      
      if (product && product.promotions) {
        const hasPromo = product.promotions.some((p: any) => (p.promotionId || p.id) === promoId);
        if (hasPromo) {
          return item.quantity;
        }
      }
    }
    return 0;
  };

  const isUsageLimitExceeded = (promo: any): boolean => {
    if (promo.promotionBy !== "ByQuantity" && promo.promotionBy !== "ByValue") return false;
    if (promo.availableUsage === undefined || promo.availableUsage === null) return false;

    const customerQuantity = getCustomerQuantityForPromo(promo);
    const buyQty = Number(promo.buyQty);
    if (!buyQty || buyQty <= 0) return false;

    const requiredUsage = Math.floor(customerQuantity / buyQty);

    return requiredUsage > promo.availableUsage;
  };

  // Derive the current Redux selection as a unified key so we can:
  //   1. Initialize selectedKey on mount (picks up an existing selection from another instance)
  //   2. Mirror Redux → local state when an external instance changes it
  const keyFromRedux = reduxCartPromoId
    ? `cart:${reduxCartPromoId}`
    : selectedGrandPromo?.id != null
    ? `overall:${selectedGrandPromo.id}`
    : null;

  // Unified selection key: "cart:{promoKey}" | "overall:{promoId}" | null
  // Initialize from Redux so a second instance (e.g. sidebar opening after checkout auto-select)
  // immediately shows the already-selected promotion.
  const [selectedKey, setSelectedKey] = useState<string | null>(keyFromRedux);

  // ─── Overall promo helpers ───────────────────────────────────────────────

  const isOverallExpired = (promo: any): boolean => {
    if (!promo) return true;
    if (promo.isActive !== "Y") return true;
    if (promo.startDate && promo.endDate && !isClientPromotionActive(promo, now)) return true;
    return false;
  };

  const isOverallApplicable = (promo: any): boolean => {
    if (isOverallExpired(promo)) return false;
    if (!promo.promotionValue) return false;
    if (baseFinalTotal < (promo.fromPrice || 0)) return false;
    if (baseFinalTotal > (promo.toPrice || Infinity)) return false;
    if (promo.promotionType === "INR" && promo.promotionValue >= baseFinalTotal) return false;
    // Discount value must not exceed the slab's upper price limit
    if (promo.promotionType === "INR" && promo.toPrice && promo.promotionValue > promo.toPrice) return false;
    return true;
  };

  // ─── Cart promo helpers ──────────────────────────────────────────────────

  const allFreeBookIds = useMemo(() => {
    const ids = new Set<string>();
    Object.values(byQuantityPromoItemsMap).forEach((items) =>
      items.forEach((i) => ids.add(i.id)),
    );
    return Array.from(ids);
  }, [byQuantityPromoItemsMap]);

  const { data: allFreeBookData, isFetching: freeBookLoading } = useGetAllCartWithIdQuery(
    { cartItems: allFreeBookIds },
    { skip: allFreeBookIds.length === 0 || !showPromotion },
  );
  const allFreeBooks: any[] = allFreeBookData?.products || [];

  // Keep a ByQuantity promo only if threshold is met AND free book has enough stock.
  // Non-ByQuantity cart promos (ByValue) are always kept — they have no stock gate.
  const availableCartPromotions = useMemo(() => {
    return cartPromotions.filter((promo) => {
      if (promo.promotionBy !== "ByQuantity") return true;
      const promoKey = String(promo.promotionId || promo.id);
      const freeItems = byQuantityPromoItemsMap[promoKey] || [];
      if (freeItems.length === 0) return false;
      return freeItems.some((fi) => {
        const book = allFreeBooks.find((b) => b.product.item === fi.id);
        if (!book) return true; // still loading — keep visible
        return (book.product.stockQty ?? 0) >= fi.qty;
      });
    });
  }, [cartPromotions, byQuantityPromoItemsMap, allFreeBooks]);

  // Auto-select logic: overall promos are hidden when cart promos are available (priority rule).
  const visibleOverallDiscounts = useMemo(
    () =>
      availableCartPromotions.length > 0
        ? []
        : discounts.filter((promo) => !isOverallExpired(promo)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableCartPromotions.length, discounts, now],
  );

  // Display: always show all non-expired overall promos, even when cart promos exist.
  const allOverallDiscounts = useMemo(
    () => discounts.filter((promo) => !isOverallExpired(promo)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [discounts, now],
  );

  // Returns false for unmet ByQuantity promos (threshold or stock not met).
  const isCartPromoMet = (promo: any): boolean => {
    if (promo.promotionBy !== "ByQuantity") return true;
    const promoKey = String(promo.promotionId || promo.id);
    const freeItems = byQuantityPromoItemsMap[promoKey] || [];
    if (freeItems.length === 0) return false;
    return freeItems.some((fi: any) => {
      const book = allFreeBooks.find((b: any) => b.product.item === fi.id);
      if (!book) return true; // still loading — optimistically keep
      return (book.product.stockQty ?? 0) >= fi.qty;
    });
  };

  // ─── Value calculation for auto-select ──────────────────────────────────

  const getCartPromoValue = (promo: any): number => {
    if (promo.promotionBy === "ByQuantity") {
      const promoKey = String(promo.promotionId || promo.id);
      const freeItems = byQuantityPromoItemsMap[promoKey] || [];
      return freeItems.reduce((sum, fi) => {
        const book = allFreeBooks.find((b) => b.product.item === fi.id);
        return sum + (book ? Number(book.product.ecomUnitPrice) || 0 : 0) * fi.qty;
      }, 0);
    }
    if (promo.promotionBy === "ByValue") {
      const val = promo.promotion ?? 0;
      return promo.promotionType === "%" ? (baseFinalTotal * val) / 100 : val;
    }
    return 0;
  };

  const getOverallValue = (promo: any): number => {
    if (!isOverallApplicable(promo)) return 0;
    if (promo.promotionType === "%") return (baseFinalTotal * promo.promotionValue) / 100;
    if (promo.promotionType === "INR") return promo.promotionValue;
    return 0;
  };

  // ─── Auto-select: pick the promotion that saves the customer the most ───

  // Keep baseFinalTotal in a ref so the auto-select effect can read the latest value without
  // listing it as a dependency. Including it as a dep would cause an infinite loop:
  // select promo → calculateDiscount() changes total → effect re-runs → isOverallApplicable
  // fails (total now outside promo range after discount) → deselects → total resets → loop.
  const baseFinalTotalRef = useRef(baseFinalTotal);
  useEffect(() => { baseFinalTotalRef.current = baseFinalTotal; });

  useEffect(() => {
    if (!showPromotion) return;
    // Secondary instances (e.g. sidebar on checkout page) must not auto-select — the primary
    // instance (PriceDetails) already owns that responsibility. Manual clicks still work.
    if (disableAutoSelect) return;
    // Wait until free-book data has actually arrived before evaluating ByQuantity promos.
    // Using !allFreeBookData (not just isFetching) because RTK Query's first-render isFetching
    // can be false for one frame before the request fires, causing valueOf(ByQuantity) = 0.
    if (allFreeBookIds.length > 0 && (!allFreeBookData || freeBookLoading)) return;

    // Use the ref so changes caused by our own discount dispatch don't re-trigger this effect.
    const total = baseFinalTotalRef.current;

    const isApplicable = (promo: any): boolean => {
      if (isOverallExpired(promo)) return false;
      if (!promo.promotionValue) return false;
      if (total < (promo.fromPrice || 0)) return false;
      if (total > (promo.toPrice || Infinity)) return false;
      if (promo.promotionType === "INR" && promo.promotionValue >= total) return false;
      if (promo.promotionType === "INR" && promo.toPrice && promo.promotionValue > promo.toPrice) return false;
      return true;
    };

    const valueOf = (promo: any): number => {
      if (promo.promotionBy === "ByQuantity") {
        const promoKey = String(promo.promotionId || promo.id);
        const freeItems = byQuantityPromoItemsMap[promoKey] || [];
        return freeItems.reduce((sum: number, fi: any) => {
          const book = allFreeBooks.find((b: any) => b.product.item === fi.id);
          return sum + (book ? Number(book.product.ecomUnitPrice) || 0 : 0) * fi.qty;
        }, 0);
      }
      if (promo.promotionBy === "ByValue") {
        const val = promo.promotion ?? 0;
        if (promo.promotionType === "%") return (total * val) / 100;
        // INR: multiply by total qualifying quantity across all source products.
        // e.g. ₹50 off per book × 4 books = ₹200 true savings — must beat ByQuantity fairly.
        return val * (promo._totalQty || 1);
      }
      return 0;
    };

    const overallValueOf = (promo: any): number => {
      if (!isApplicable(promo)) return 0;
      if (promo.promotionType === "%") return (total * promo.promotionValue) / 100;
      if (promo.promotionType === "INR") return promo.promotionValue;
      return 0;
    };

    // Keep current selection if still valid
    const currentValid =
      selectedKey &&
      ((selectedKey.startsWith("cart:") &&
        availableCartPromotions.some(
          (p) => `cart:${String(p.promotionId || p.id)}` === selectedKey && !isUsageLimitExceeded(p),
        )) ||
        (selectedKey.startsWith("overall:") &&
          visibleOverallDiscounts.some(
            (p) => `overall:${p.id}` === selectedKey && isApplicable(p) && !isUsageLimitExceeded(p),
          )));
    if (currentValid) return;

    let bestKey: string | null = null;
    let bestValue = -1;

    availableCartPromotions.forEach((promo) => {
      if (isUsageLimitExceeded(promo)) return;
      const key = `cart:${String(promo.promotionId || promo.id)}`;
      const val = valueOf(promo);
      if (val > bestValue) { bestValue = val; bestKey = key; }
    });

    visibleOverallDiscounts.forEach((promo) => {
      if (!isApplicable(promo)) return;
      if (isUsageLimitExceeded(promo)) return;
      const key = `overall:${promo.id}`;
      const val = overallValueOf(promo);
      if (val > bestValue) { bestValue = val; bestKey = key; }
    });

    setSelectedKey(bestKey);
    // baseFinalTotal is intentionally excluded — read via ref to avoid the oscillation loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCartPromotions, visibleOverallDiscounts, allFreeBooks, byQuantityPromoItemsMap, showPromotion, freeBookLoading, now]);

  // ─── Sync selectedKey → Redux selectedGrandPromo ────────────────────────

  const selectedGrandPromoRef = useRef(selectedGrandPromo);
  useEffect(() => { selectedGrandPromoRef.current = selectedGrandPromo; });

  useEffect(() => {
    if (!showPromotion) return;
    const current = selectedGrandPromoRef.current;

    if (selectedKey?.startsWith("overall:")) {
      const id = Number(selectedKey.replace("overall:", ""));
      // Find the promo in visibleOverallDiscounts — it is already filtered to non-expired.
      // Do NOT re-check isOverallApplicable here: that function reads baseFinalTotal which
      // changes after calculateDiscount() is dispatched, causing a 1-second oscillation loop.
      // The auto-select effect is responsible for validity; this effect just mirrors the key → Redux.
      const promo = visibleOverallDiscounts.find((d) => d.id === id);
      if (promo) {
        if (current?.id !== promo.id) dispatch(setSelectedGrandPromo(promo));
      } else {
        if (current !== null) dispatch(setSelectedGrandPromo(null));
      }
      dispatch(setSelectedCartPromoId(null));
      dispatch(calculateDiscount());
    } else if (selectedKey?.startsWith("cart:")) {
      const cartKey = selectedKey.replace("cart:", "");
      dispatch(setSelectedCartPromoId(cartKey));
      dispatch(calculateDiscount());
      if (current !== null) dispatch(setSelectedGrandPromo(null));
    } else {
      if (current !== null) dispatch(setSelectedGrandPromo(null));
      dispatch(setSelectedCartPromoId(null));
      dispatch(calculateDiscount());
    }
    // Excluded from deps:
    // - baseFinalTotal: causes loop (calculateDiscount() changes total → effect re-runs)
    // - now: already captured through visibleOverallDiscounts; redundant here and causes
    //   per-second re-runs that re-check isOverallApplicable with the post-discount total
    // - availableCartPromotions: not used in this effect body
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, visibleOverallDiscounts, showPromotion, dispatch]);

  // ─── Sync selectedKey ← Redux (mirrors changes from a sibling instance) ──

  // When PriceDetails' instance auto-selects or the user clicks in the other instance,
  // Redux changes and keyFromRedux changes. We mirror that into local state so both UIs
  // show the same selection. The identity guard prevents a set when our own dispatch
  // already produced the current keyFromRedux (avoids a redundant render cycle).
  useEffect(() => {
    setSelectedKey((prev) => (prev === keyFromRedux ? prev : keyFromRedux));
  }, [keyFromRedux]);

  // ─── Early return ────────────────────────────────────────────────────────

  if (!showPromotion) return null;
  if (cartPromotions.length === 0 && visibleOverallDiscounts.length === 0) return null;

  // ─── Derived display data ────────────────────────────────────────────────

  const selectedCartKey = selectedKey?.startsWith("cart:")
    ? selectedKey.replace("cart:", "")
    : null;
  const selectedCartPromo = availableCartPromotions.find(
    (p) => String(p.promotionId || p.id) === selectedCartKey,
  );
  const selectedOverallId = selectedKey?.startsWith("overall:")
    ? Number(selectedKey.replace("overall:", ""))
    : null;
  const selectedOverallPromo = selectedOverallId != null
    ? allOverallDiscounts.find((d) => d.id === selectedOverallId)
    : null;

  const selectedPromoFreeItems =
    selectedCartPromo?.promotionBy === "ByQuantity" && selectedCartKey
      ? byQuantityPromoItemsMap[selectedCartKey] || []
      : [];
  const selectedFreeBooks = allFreeBooks.filter((b) =>
    selectedPromoFreeItems.some((fi) => fi.id === b.product.item),
  );

  // mode "card" → full detail for the modal card; "summary" → compact for the trigger row
  const getCartPromoDiscountText = (promo: any, mode: "card" | "summary" = "card") => {
    if (promo.promotionBy === "ByQuantity")
      return `Buy ${promo.buyQty} Get ${promo.getQty} Free`;
    const value = promo.promotion ?? promo.promotionValue;
    if (promo.promotionType === "%") {
      return `${Number(value).toFixed(numberOfDecimal)}% off`;
    }
    // INR — show total savings when quantity > 1
    const totalQty = promo._totalQty || 1;
    const perItem = Number(value);
    const totalSavings = perItem * totalQty;
    if (mode === "summary") {
      return totalQty > 1
        ? `${formatIndianCurrency(totalSavings, numberOfDecimal)} off`
        : `${formatIndianCurrency(perItem, numberOfDecimal)} off`;
    }
    // card: show breakdown so user understands the total
    return totalQty > 1
      ? `${formatIndianCurrency(perItem, numberOfDecimal)} off per item · ${formatIndianCurrency(totalSavings, numberOfDecimal)} total`
      : `${formatIndianCurrency(perItem, numberOfDecimal)} off`;
  };

  // Returns a human-readable hint for why a ByQuantity promo is locked,
  // naming the specific product the customer needs to buy more of.
  const getUnlockHint = (promo: any): string => {
    if (promo.promotionBy !== "ByQuantity") return "Not eligible yet";
    const promoKey = String(promo.promotionId || promo.id);
    // Use the dedicated promoSourceMap first, fall back to _sourceProducts on the promo object.
    const sources: Array<{ name: string; categoryName?: string; currentQty: number }> =
      promoSourceMap[promoKey] ||
      (promo._sourceProducts as Array<{ name: string; categoryName?: string; currentQty: number }> | undefined) ||
      [];
    if (sources.length > 0) {
      // Sum across all qualifying source products in the cart.
      const totalCurrentQty = sources.reduce((sum, sp) => sum + sp.currentQty, 0);
      const needed = (promo.buyQty || 0) - totalCurrentQty;
      if (needed > 0) {
        // Threshold not yet met — name the product to buy more of.
        const sp = sources[0];
        if (sp.name) {
          const name = sp.name.length > 32 ? `${sp.name.substring(0, 32)}…` : sp.name;
          return `Add ${needed} more "${name}" to unlock`;
        }
        if (sp.categoryName) {
          return `Add ${needed} more book from "${sp.categoryName}" to unlock`;
        }
        const promoLabel = promo.promotionName || promo.name;
        if (promoLabel) {
          return `Add ${needed} more qualifying book to unlock "${promoLabel}"`;
        }
      } else {
        // Threshold IS met but promo is still locked → the free item is out of stock.
        return "Free item is currently out of stock";
      }
    }
    return `Buy at least ${promo.buyQty || "?"} to unlock`;
  };

  const appliedSummaryText = (() => {
    if (selectedCartPromo)
      return `Applied: ${getCartPromoDiscountText(selectedCartPromo, "summary")}`;
    if (selectedOverallPromo && isOverallApplicable(selectedOverallPromo)) {
      const dt =
        selectedOverallPromo.promotionType === "%"
          ? `${Number(selectedOverallPromo.promotionValue).toFixed(numberOfDecimal)}% off`
          : `${formatIndianCurrency(Number(selectedOverallPromo.promotionValue), numberOfDecimal)} off`;
      return `Applied: ${dt}`;
    }
    return null;
  })();

  const totalVisible = cartPromotions.length + visibleOverallDiscounts.length;
  const hasBothSections = cartPromotions.length > 0 && visibleOverallDiscounts.length > 0;

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="mt-4 mb-4">
      {/* Trigger row */}
      <div
        onClick={() => setOpened(true)}
        className="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition shadow-sm"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-green-100 text-green-600 rounded-md">
            <Gift size={20} />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-800">Offers & Coupons</h4>
            {appliedSummaryText ? (
              <p className="text-xs text-green-600 font-medium">{appliedSummaryText}</p>
            ) : (
              <p className="text-xs text-green-600 font-medium">
                {totalVisible} offer{totalVisible !== 1 ? "s" : ""} available
              </p>
            )}
          </div>
        </div>
        <div className="text-green-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </div>
      </div>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={<div className="text-lg font-bold text-gray-800">Offers & Coupons</div>}
        centered
        size="md"
        zIndex={2000}
        styles={{
          header: { borderBottom: "1px solid #eee", paddingBottom: "10px" },
          body: { padding: "16px", backgroundColor: "#f9fafb" },
        }}
      >
        <div className="space-y-4">
          {/* <p className="text-xs text-gray-500">Only one offer can be applied at a time.</p> */}

          {/* ── Cart promotions (ByQuantity free-book / ByValue) ── */}
          {cartPromotions.length > 0 && (
            <>
              {hasBothSections && (
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Product Offers
                </p>
              )}

              {cartPromotions.map((promo, index) => {
                const promoKey = String(promo.promotionId || promo.id);
                const unifiedKey = `cart:${promoKey}`;
                const isMet = isCartPromoMet(promo);
                const isSelected = selectedKey === unifiedKey && isMet;
                const isByQty = promo.promotionBy === "ByQuantity";
                const discountText = getCartPromoDiscountText(promo);

                return (
                  <div
                    key={promoKey || index}
                    className={`bg-white rounded-xl shadow-sm overflow-hidden border ${
                      isSelected ? "border-green-400" : isMet ? "border-green-100" : "border-gray-200"
                    }`}
                  >
                    <div className={`p-4 ${!isMet ? "opacity-60" : ""}`}>
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isMet ? "bg-green-50 text-green-500" : "bg-gray-100 text-gray-400"}`}>
                            {isByQty ? <Gift size={24} /> : <Percent size={24} />}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-md font-bold text-gray-800">
                            {promo.promotionName || discountText}
                          </h4>
                          <p className="text-xs text-gray-600 mt-1">{discountText}</p>
                          {/* Issue 3: show which product(s) carry this promotion */}
                          {(() => {
                            const sources = (promo._sourceProducts as any[]) || [];
                            if (sources.length === 0) return null;
                            const label = isByQty ? "For: " : "On: ";
                            // Prefer named products (title/itemDesc resolved)
                            const named = sources.filter((sp: any) => sp.name);
                            if (named.length > 0) {
                              return (
                                <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                                  {label}
                                  {named
                                    .map((sp: any) =>
                                      sp.currentQty > 1 ? `${sp.name} (×${sp.currentQty})` : sp.name,
                                    )
                                    .join(", ")}
                                </p>
                              );
                            }
                            // No product names — fall back to category name
                            const rawCategories = sources
                              .map((sp: any) => sp.categoryName as string)
                              .filter(Boolean);
                            const categories = rawCategories.filter(
                              (v, i) => rawCategories.indexOf(v) === i,
                            );
                            if (categories.length > 0) {
                              return (
                                <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                                  {label}
                                  {categories.join(", ")} category
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>

                      <div className="mt-3 flex justify-end">
                        {isSelected ? (
                          <span className="text-sm font-bold text-green-600 flex items-center gap-1">
                            Applied
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          </span>
                        ) : isMet ? (
                          <button
                            onClick={() => {
                              if (isUsageLimitExceeded(promo)) {
                                notifications.show({
                                  message: `Available usage of this promotion is ${promo.availableUsage}.`,
                                  color: "red",
                                });
                              } else {
                                setSelectedKey(unifiedKey);
                              }
                            }}
                            className="text-sm font-bold text-green-600 hover:text-green-700 transition border border-green-500 px-3 py-1 rounded-md"
                          >
                            Apply this offer
                          </button>
                        ) : (
                          /* Issue 1: tell the user which specific product to buy more of */
                          <span className="text-xs text-gray-400 italic">
                            {getUnlockHint(promo)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Free books for the selected ByQuantity promo */}
                    {isSelected && isByQty && (
                      <div className="border-t border-green-100 px-4 py-3 bg-green-50">
                        {freeBookLoading ? (
                          <p className="text-xs text-green-700 animate-pulse">
                            Loading free books...
                          </p>
                        ) : selectedFreeBooks.length > 0 ? (
                          <>
                            <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                              <Gift size={13} /> Free books included
                            </p>
                            {selectedFreeBooks.map((book) => {
                              const freeQty = selectedPromoFreeItems.find(
                                (fi) => fi.id === book.product.item,
                              )?.qty;
                              return (
                                <div
                                  key={book.product.item}
                                  className="flex items-start gap-3 mt-2"
                                >
                                  <Link
                                    to={`/product?pid=${encodeParam(book.product.item)}`}
                                    onClick={() => {
                                      setOpened(false);
                                      window.scrollTo(0, 0);
                                    }}
                                    className="flex-shrink-0"
                                  >
                                    <CartPromoImage
                                      src={book.imagePath || ""}
                                      alt={book.product.itemDesc}
                                      className="w-14 h-18 object-cover rounded border border-vintageBorder"
                                    />
                                  </Link>
                                  <div className="min-w-0 flex-1">
                                    <Link
                                      to={`/product?pid=${encodeParam(book.product.item)}`}
                                      onClick={() => {
                                        setOpened(false);
                                        window.scrollTo(0, 0);
                                      }}
                                      className="text-sm font-semibold text-vintageText truncate block hover:underline"
                                    >
                                      {book.product.itemDesc}
                                    </Link>
                                    <div className="flex gap-2 mt-1">
                                      <span className="inline-block text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded">
                                        Free Item
                                      </span>
                                      {freeQty && (
                                        <span className="inline-block text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded">
                                          Qty: {freeQty}
                                        </span>
                                      )}
                                    </div>
                                    {book.product.author && (
                                      <p className="text-xs text-gray-600 truncate mt-1">
                                        {book.product.author}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <p className="text-xs text-green-700">
                            Free books will be added at checkout.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* ── Overall / ecommerce-wide discounts ── */}
          {visibleOverallDiscounts.length > 0 && (
            <>
              {hasBothSections && (
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-2">
                  Order Discounts
                </p>
              )}

              {visibleOverallDiscounts.map((promo, index) => {
                const applicable = isOverallApplicable(promo);
                // Blocked only when cart promos are actually available (met threshold + in stock).
                // Unmet ByQuantity promos don't block overall promos.
                const blockedByCart = availableCartPromotions.length > 0;
                const isSelectable = applicable && !blockedByCart;
                const unifiedKey = `overall:${promo.id}`;
                const isSelected = selectedKey === unifiedKey && isSelectable;
                const discountText =
                  promo.promotionType === "%"
                    ? `${Number(promo.promotionValue).toFixed(numberOfDecimal)}% off`
                    : `${formatIndianCurrency(Number(promo.promotionValue), numberOfDecimal)} off`;

                const hintText = "Shop more to use this coupon";

                return (
                  <div
                    key={promo.id || index}
                    className={`bg-white rounded-xl shadow-sm overflow-hidden border ${
                      isSelected
                        ? "border-green-400"
                        : isSelectable
                          ? "border-pink-100"
                          : "border-gray-200"
                    }`}
                  >
                    <div className={`p-4 relative ${!isSelectable ? "opacity-70" : ""}`}>
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div
                            className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              isSelectable
                                ? "bg-pink-50 text-pink-500"
                                : "bg-gray-100 text-gray-400"
                            }`}
                          >
                            <Percent size={24} />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-md font-bold text-gray-800">
                            Extra {discountText}
                          </h4>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                            {promo.discountDesc || "Applicable on your order"}
                          </p>
                        </div>
                      </div>

                      <div
                        className={`mt-4 p-3 border border-dashed rounded-lg flex items-center justify-between ${
                          isSelectable
                            ? "border-pink-300 bg-pink-50/50"
                            : "border-gray-300 bg-gray-50"
                        }`}
                      >
                        <span className="text-sm font-semibold text-gray-700 tracking-wider">
                          {promo.discountDesc?.substring(0, 10).toUpperCase() || "OFFER"}
                        </span>

                        {isSelected ? (
                          <button
                            onClick={() => setSelectedKey(null)}
                            className="text-sm font-bold text-green-600 flex items-center gap-1 hover:text-green-700"
                          >
                            Applied
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          </button>
                        ) : isSelectable ? (
                          <button
                            onClick={() => {
                              if (isUsageLimitExceeded(promo)) {
                                notifications.show({
                                  message: `Available usage of this promotion is ${promo.availableUsage}.`,
                                  color: "red",
                                });
                              } else {
                                setSelectedKey(unifiedKey);
                              }
                            }}
                            className="text-sm font-bold text-pink-600 hover:text-pink-700 transition"
                          >
                            Apply
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Not applicable</span>
                        )}
                      </div>
                    </div>

                    {!isSelectable && (
                      <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 font-medium">
                        {hintText}
                      </div>
                    )}
                    {isSelected && (
                      <div className="bg-green-50 px-4 py-2 text-xs text-green-700 font-medium">
                        Hurray! You saved extra on this order.
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default OrderDiscountSelector;
